interface ValidationResult {
  isValid: boolean;
  message?: string;
}

interface SchemaErrors {
  [key: string]: string | SchemaErrors;
}

interface ValidateResult<T extends object = Record<string, any>> {
  errors: SchemaErrors;
  errorFields: string[];
  isValid: boolean;
  body: T;
}

type JSTypeof =
  | "string"
  | "number"
  | "boolean"
  | "object"
  | "undefined"
  | "function";

export class ValidationError extends Error {
  public result: ValidateResult;
  public message: string;

  constructor(result: ValidateResult) {
    super("Validation failed");
    this.name = "ValidationError";
    this.result = result;
    this.message = Object.values(result.errors).join(", ");
  }

  toJSON() {
    return this.result;
  }
}

class ValidateField {
  private validators: Array<(value: any) => ValidationResult> = [];
  private isRequiredField = false;
  private requiredValidatorRef: ((value: any) => ValidationResult) | null =
    null;

  public required(options?: { message?: string }) {
    this.isRequiredField = true;
    this.requiredValidatorRef = (value: any): ValidationResult => {
      if (value === undefined || value === null || value === "") {
        return {
          isValid: false,
          message: options?.message || "Field is required",
        };
      }
      return { isValid: true };
    };
    this.validators.push(this.requiredValidatorRef);
    return this;
  }

  public clone(): ValidateField {
    const cloned = new ValidateField();
    cloned.validators = [...this.validators];
    cloned.isRequiredField = this.isRequiredField;
    cloned.requiredValidatorRef = this.requiredValidatorRef;
    return cloned;
  }

  public removeRequired(): ValidateField {
    const cloned = new ValidateField();
    cloned.validators = this.validators.filter(
      (v) => v !== this.requiredValidatorRef
    );
    cloned.isRequiredField = false;
    cloned.requiredValidatorRef = null;
    return cloned;
  }

  public string(options?: { message?: string }) {
    this.validators.push((value: any) => {
      if (
        !this.isRequiredField &&
        (value === undefined || value === null || value === "")
      ) {
        return { isValid: true };
      }
      if (typeof value !== "string") {
        return {
          isValid: false,
          message: options?.message || "Must be a string",
        };
      }
      return { isValid: true };
    });
    return this;
  }

  public number(options?: { message?: string }) {
    this.validators.push((value: any) => {
      if (
        !this.isRequiredField &&
        (value === undefined || value === null || value === "")
      ) {
        return { isValid: true };
      }
      if (typeof value !== "number" || isNaN(value)) {
        return {
          isValid: false,
          message: options?.message || "Must be a number",
        };
      }
      return { isValid: true };
    });
    return this;
  }

  public array(arrayType?: JSTypeof | Schema, options?: { message?: string }) {
    this.validators.push((value: any) => {
      if (!this.isRequiredField && (value === undefined || value === null)) {
        return { isValid: true };
      }
      if (!Array.isArray(value)) {
        return {
          isValid: false,
          message: options?.message || "Must be an array",
        };
      }
      // If an element type/schema is provided, validate each element
      if (arrayType) {
        // Primitive type validation (JSTypeof)
        if (typeof arrayType === "string") {
          for (let i = 0; i < value.length; i++) {
            const el = value[i];
            if (arrayType === "number") {
              if (typeof el !== "number" || isNaN(el)) {
                return {
                  isValid: false,
                  message:
                    options?.message ||
                    `Array elements must be ${arrayType} (invalid at index ${i})`,
                };
              }
            } else {
              if (typeof el !== arrayType) {
                return {
                  isValid: false,
                  message:
                    options?.message ||
                    `Array elements must be ${arrayType} (invalid at index ${i})`,
                };
              }
            }
          }
        } else if (arrayType instanceof Schema) {
          // Use Schema.validate with throws disabled to collect result
          for (let i = 0; i < value.length; i++) {
            const el = value[i];
            const res = arrayType.validate(el, { throwsError: false });
            if (!res.isValid) {
              return {
                isValid: false,
                message:
                  options?.message ||
                  `Array element at index ${i} failed schema validation`,
              };
            }
          }
        }
      }

      return { isValid: true };
    });
    return this;
  }

  public object(schema?: Schema, options?: { message?: string }) {
    this.validators.push((value: any) => {
      if (!this.isRequiredField && (value === undefined || value === null)) {
        return { isValid: true };
      }
      if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return {
          isValid: false,
          message: options?.message || "Must be an object",
        };
      }
      if (schema) {
        const res = schema.validate(value, { throwsError: false });
        if (!res.isValid) {
          return {
            isValid: false,
            message: options?.message || "Object validation failed",
          };
        }
      }
      return { isValid: true };
    });
    return this;
  }

  public minLength(length: number, options?: { message?: string }) {
    this.validators.push((value: any) => {
      if (
        !this.isRequiredField &&
        (value === undefined || value === null || value === "")
      ) {
        return { isValid: true };
      }
      if (typeof value !== "string" || value.length < length) {
        return {
          isValid: false,
          message:
            options?.message || `Must be at least ${length} characters long`,
        };
      }
      return { isValid: true };
    });
    return this;
  }

  public maxLength(length: number, options?: { message?: string }) {
    this.validators.push((value: any) => {
      if (
        !this.isRequiredField &&
        (value === undefined || value === null || value === "")
      ) {
        return { isValid: true };
      }
      if (typeof value !== "string" || value.length > length) {
        return {
          isValid: false,
          message:
            options?.message || `Must be at most ${length} characters long`,
        };
      }
      return { isValid: true };
    });
    return this;
  }

  public cpf(options?: { message?: string }) {
    this.validators.push((value: any) => {
      if (
        !this.isRequiredField &&
        (value === undefined || value === null || value === "")
      ) {
        return { isValid: true };
      }
      const cpf = String(value).replace(/\D/g, "");
      if (cpf.length !== 11) {
        return { isValid: false, message: options?.message || "Invalid CPF" };
      }

      // Verifica se todos os dígitos são iguais
      if (/^(\d)\1{10}$/.test(cpf)) {
        return { isValid: false, message: options?.message || "Invalid CPF" };
      }

      // Validação dos dígitos verificadores
      let sum = 0;
      for (let i = 0; i < 9; i++) {
        sum += parseInt(cpf.charAt(i)) * (10 - i);
      }
      let digit = 11 - (sum % 11);
      if (digit >= 10) digit = 0;
      if (digit !== parseInt(cpf.charAt(9))) {
        return { isValid: false, message: options?.message || "Invalid CPF" };
      }

      sum = 0;
      for (let i = 0; i < 10; i++) {
        sum += parseInt(cpf.charAt(i)) * (11 - i);
      }
      digit = 11 - (sum % 11);
      if (digit >= 10) digit = 0;
      if (digit !== parseInt(cpf.charAt(10))) {
        return { isValid: false, message: options?.message || "Invalid CPF" };
      }

      return { isValid: true };
    });
    return this;
  }

  public email(options?: { message?: string }) {
    this.validators.push((value: any) => {
      if (
        !this.isRequiredField &&
        (value === undefined || value === null || value === "")
      ) {
        return { isValid: true };
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(String(value))) {
        return {
          isValid: false,
          message: options?.message || "Invalid email",
        };
      }
      return { isValid: true };
    });
    return this;
  }

  public date(options?: { message?: string }) {
    this.validators.push((value: any) => {
      if (
        !this.isRequiredField &&
        (value === undefined || value === null || value === "")
      ) {
        return { isValid: true };
      }
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return { isValid: false, message: options?.message || "Invalid date" };
      }
      return { isValid: true };
    });
    return this;
  }

  public boolean(options?: { message?: string }) {
    this.validators.push((value: any) => {
      if (!this.isRequiredField && (value === undefined || value === null)) {
        return { isValid: true };
      }
      if (typeof value !== "boolean") {
        return {
          isValid: false,
          message: options?.message || "Must be a boolean",
        };
      }
      return { isValid: true };
    });
    return this;
  }

  public file(options?: { message?: string }) {
    this.validators.push((value: any) => {
      if (!this.isRequiredField && (value === undefined || value === null)) {
        return { isValid: true };
      }
      if (typeof File !== "undefined" && value instanceof File) {
        return { isValid: true };
      }
      if (
        typeof value === "object" &&
        value !== null &&
        ("name" in value || "type" in value)
      ) {
        return { isValid: true };
      }
      return {
        isValid: false,
        message: options?.message || "Must be a file",
      };
    });
    return this;
  }

  public enum(possibleValues: any[], options?: { message?: string }) {
    this.validators.push((value: any) => {
      if (
        !this.isRequiredField &&
        (value === undefined || value === null || value === "")
      ) {
        return { isValid: true };
      }
      if (!possibleValues.includes(value)) {
        return {
          isValid: false,
          message:
            options?.message || `Must be one of: ${possibleValues.join(", ")}`,
        };
      }
      return { isValid: true };
    });
    return this;
  }

  public instance(
    constructor: new (...args: any[]) => any,
    options?: { message?: string }
  ) {
    this.validators.push((value: any) => {
      if (!this.isRequiredField && (value === undefined || value === null)) {
        return { isValid: true };
      }
      if (!(value instanceof constructor)) {
        return {
          isValid: false,
          message:
            options?.message || `Must be an instance of ${constructor.name}`,
        };
      }
      return { isValid: true };
    });
    return this;
  }

  public custom(validator: (value: any) => ValidationResult) {
    this.validators.push(validator);
    return this;
  }

  public validate(value: any): ValidationResult {
    for (const validator of this.validators) {
      const result = validator(value);
      if (!result.isValid) {
        return result;
      }
    }
    return { isValid: true };
  }
}

type SchemaDefinition = {
  [key: string]: ValidateField | SchemaDefinition;
};

export class Schema {
  private schema: SchemaDefinition;

  constructor(schema: SchemaDefinition) {
    this.schema = schema;
  }

  private cloneSchemaDefinition(
    schemaDef: SchemaDefinition,
    removeRequired: boolean = false
  ): SchemaDefinition {
    const cloned: SchemaDefinition = {};
    for (const key in schemaDef) {
      const field = schemaDef[key];
      if (field instanceof ValidateField) {
        cloned[key] = removeRequired ? field.removeRequired() : field.clone();
      } else {
        // Nested schema
        cloned[key] = this.cloneSchemaDefinition(field, removeRequired);
      }
    }
    return cloned;
  }

  public removeAllRequired(): Schema {
    const clonedSchema = this.cloneSchemaDefinition(this.schema, true);
    return new Schema(clonedSchema);
  }

  private filterDataBySchema(schema: SchemaDefinition, data: any): any {
    const filtered: any = {};
    const dataObj = data as Record<string, any>;

    for (const key in schema) {
      if (data && typeof data === "object" && key in data) {
        const field = schema[key];
        if (field instanceof ValidateField) {
          filtered[key] = dataObj[key];
        } else {
          // Nested schema
          filtered[key] = this.filterDataBySchema(field, dataObj[key]);
        }
      }
    }

    return filtered;
  }

  private validateField(
    field: ValidateField | SchemaDefinition,
    value: any,
    path: string = ""
  ): {
    errors: string | SchemaErrors;
    errorFields: string[];
  } {
    let errors: string | SchemaErrors = {};
    const errorFields: string[] = [];

    if (field instanceof ValidateField) {
      const result = field.validate(value);
      if (!result.isValid) {
        if (path) {
          errorFields.push(path);
          return { errors: result.message || "Validation error", errorFields };
        }
      }
    } else {
      // É um objeto nested
      const nestedErrors: SchemaErrors = {};
      for (const key in field) {
        const nestedPath = path ? `${path}.${key}` : key;
        const nestedValue = value?.[key];
        const nestedResult = this.validateField(
          field[key],
          nestedValue,
          nestedPath
        );

        if (nestedResult.errorFields.length > 0) {
          nestedErrors[key] = nestedResult.errors;
          errorFields.push(...nestedResult.errorFields);
        }
      }
      errors = nestedErrors;
    }

    return { errors, errorFields };
  }

  public validate<T extends object = Record<string, any>>(
    data: T,
    options?: { throwsError?: boolean }
  ): ValidateResult<T> {
    const dataToValidate = data;
    const dataObj = dataToValidate as Record<string, any>;
    const filteredData = this.filterDataBySchema(this.schema, dataToValidate);
    const allErrors: SchemaErrors = {};
    const allErrorFields: string[] = [];

    for (const key in this.schema) {
      const field = this.schema[key];
      const value = dataObj[key];
      const result = this.validateField(field, value, key);

      if (result.errorFields.length > 0) {
        allErrors[key] = result.errors;
        allErrorFields.push(...result.errorFields);
      }
    }

    const validateResult: ValidateResult<T> = {
      errors: allErrors,
      errorFields: allErrorFields,
      isValid: allErrorFields.length === 0,
      body: filteredData,
    };

    const throws = options?.throwsError ?? true;
    if (throws && !validateResult.isValid) {
      throw new ValidationError(validateResult);
    }

    return validateResult;
  }

  public static validateField() {
    return new ValidateField();
  }
}
